import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DATE_FORMATS } from "../../../../constants";
import Dropdown from "../../../../parts/dropdown";
import DateInput from "../../../../parts/inputs/date-input";
import { RANGES, RESULTS, todayIn } from "../door-access-utils";
import DoorsFilter from "./DoorsFilter";
import MemberFilter from "./MemberFilter";
import QuickToggle from "./QuickToggle";

const MAX_CUSTOM_DAYS = 366;
const DAY = "YYYY-MM-DD";

const toDate = (day) => (day ? moment(day, DAY).toDate() : undefined);
const toDay = (picked) =>
  moment(picked, DATE_FORMATS.DATE_PICKER_FORMAT).format(DAY);

const validCustom = (from, to) => {
  if (!from || !to) return false;
  const start = moment.utc(from, "YYYY-MM-DD");
  const end = moment.utc(to, "YYYY-MM-DD");
  return (
    start.isValid() &&
    end.isValid() &&
    !end.isBefore(start) &&
    end.diff(start, "days") + 1 <= MAX_CUSTOM_DAYS
  );
};

export const CustomRange = ({ filters, timeZone, onApply }) => {
  const { t } = useTranslation("coaching-portal");
  const [from, setFrom] = useState(filters.customFrom);
  const [to, setTo] = useState(filters.customTo);
  const today = todayIn(timeZone);
  const ok = validCustom(from, to);

  return (
    <div className="door-custom-range">
      <div className="door-custom-range__field">
        <span>{t("doorAccess.filters.customFrom")}</span>
        <DateInput
          name="door-from"
          value={toDate(from)}
          max={toDate(to) ?? toDate(today)}
          onChange={(picked) => setFrom(toDay(picked))}
        />
      </div>
      <div className="door-custom-range__field">
        <span>{t("doorAccess.filters.customTo")}</span>
        <DateInput
          name="door-to"
          value={toDate(to)}
          min={toDate(from)}
          max={toDate(today)}
          onChange={(picked) => setTo(toDay(picked))}
          alignRight
        />
      </div>
      <button
        type="button"
        data-button-type="primary"
        sz="small" //NOSONAR
        disabled={!ok}
        onClick={() => onApply(from, to)}
      >
        {t("doorAccess.filters.customApply")}
      </button>
      {!ok && from && to && (
        <span className="door-custom-range__hint">
          {t("doorAccess.filters.customHint")}
        </span>
      )}
    </div>
  );
};

const LogsFilters = ({
  api,
  filters,
  timeZone,
  doors,
  unmatchedCount,
  canExport,
  exporting,
  onChange,
  onExport,
}) => {
  const { t } = useTranslation("coaching-portal");
  const [customOpen, setCustomOpen] = useState(filters.range === "custom");

  useEffect(() => {
    setCustomOpen(filters.range === "custom");
  }, [filters.range]);

  const shownRange = customOpen ? "custom" : filters.range;
  const update = (patch) => onChange({ ...filters, ...patch, page: 1 });

  return (
    <div className="door-filters">
      <div className="door-filters__bar">
        <Dropdown
          options={RANGES.map((range) => ({
            value: range,
            text: t(`doorAccess.filters.range.${range}`),
          }))}
          selectedValue={shownRange}
          summaryText={t(`doorAccess.filters.range.${shownRange}`)}
          onSelectFn={(range) => {
            if (range === "custom") setCustomOpen(true);
            else update({ range, customFrom: "", customTo: "" });
          }}
        />

        <MemberFilter
          api={api}
          member={filters.member}
          onSelect={(member) => update({ member })}
        />

        <DoorsFilter
          doors={doors}
          selected={filters.doors}
          onChange={(selected) => update({ doors: selected })}
        />

        <QuickToggle
          label={t("doorAccess.filters.resultLabel")}
          value={filters.result}
          options={RESULTS.map((result) => ({
            value: result,
            label: t(`doorAccess.filters.result.${result}`),
          }))}
          onSelect={(result) => update({ result })}
        />

        <div className="door-switch-label">
          <input
            type="checkbox"
            role="switch"
            aria-checked={filters.unmatchedOnly}
            aria-label={t("doorAccess.filters.unmatchedOnly")}
            checked={filters.unmatchedOnly}
            onChange={() => update({ unmatchedOnly: !filters.unmatchedOnly })}
          />
          <span>{t("doorAccess.filters.unmatchedOnly")}</span>
          <span className="door-count">{unmatchedCount}</span>
        </div>

        <span className="door-grow" />

        {canExport && (
          <button
            type="button"
            data-button-type="primary"
            sz="small" //NOSONAR
            disabled={exporting}
            onClick={onExport}
          >
            {exporting
              ? t("doorAccess.filters.exporting")
              : t("doorAccess.filters.export")}
          </button>
        )}
      </div>

      {customOpen && (
        <CustomRange
          filters={filters}
          timeZone={timeZone}
          onApply={(customFrom, customTo) =>
            update({ range: "custom", customFrom, customTo })
          }
        />
      )}
    </div>
  );
};

export default LogsFilters;
